// ts-rest contract for the agent runner endpoint, plus the typed surfaces
// that supersede the legacy `/api/staff/actions/:name` registry
// (OLD_CODE.html #2): composer triggers and the reviewable approve / reject
// / request-revision family.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
    REVIEWABLE_KIND_SPECS,
    reviewableKindSchema,
    type ReviewPayload,
    type ReviewableKind,
} from '../reviewable-kind-spec';

const c = initContract();

// ---- Reviewable family (OLD_CODE.html #2 replacement) ----
//
// The reviewable kind names the artefact the staff reviewer is acting on.
// Kebab-case, deliberately not the underlying table name — the wire surface
// must not leak storage shape.
//
// The enum is no longer written here. It is DERIVED from
// `REVIEWABLE_KIND_SPECS` (`../reviewable-kind-spec.ts`): the kinds whose
// approve routes through this generic family, as opposed to the three whose
// approve carries a bespoke effect (VIGIL raises an alert, AEGIS-Breach writes
// the register, SCRIVENER materialises a letter). Two hand-kept lists used to
// hold that split, and only a comment bound them.
//
// The server registry supplies what this layer must never hold: the Object
// Type, the personas, the materialise hook and the precheck (ADR-0003).
export { reviewableKindSchema, type ReviewableKind } from '../reviewable-kind-spec';

// Composer agents that the `triggerComposer` endpoint dispatches to.
export const composerAgentSchema = z.enum([
    'scrivener',
    'herald',
    'emissary',
    'charter',
    'haven',
    'forum',
    'arbiter',
]);
export type ComposerAgent = z.infer<typeof composerAgentSchema>;

// Shared response envelope for the typed endpoints that supersede the
// `/api/staff/actions/:name` surface. Preserves `{ ok, data, invalidate }`
// so the existing React-Query cache-bust logic in the web doesn't change.
export const staffActionOkSchema = z.object({
    ok: z.literal(true),
    data: z.record(z.string(), z.unknown()).optional(),
    invalidate: z.array(z.string()).optional(),
});
export const staffActionErrorSchema = z.object({
    ok: z.literal(false),
    error: z.string(),
});
export type StaffActionOk = z.infer<typeof staffActionOkSchema>;
export type StaffActionError = z.infer<typeof staffActionErrorSchema>;

// The reviewable / approve / reject / request-revision / composer endpoints all
// answer with the same staff-action response shape: a `staffActionOkSchema` 200
// and `staffActionErrorSchema` for 400/403/(404). Shared response maps so the
// quad isn't copy-pasted per endpoint. ts-rest reads `responses` structurally,
// so referencing a shared const is behaviour-preserving.
export const staffActionResponses = {
    200: staffActionOkSchema,
    400: staffActionErrorSchema,
    403: staffActionErrorSchema,
    404: staffActionErrorSchema,
} as const;
export const staffActionResponsesNoNotFound = {
    200: staffActionOkSchema,
    400: staffActionErrorSchema,
    403: staffActionErrorSchema,
} as const;

/**
 * Discriminated union the web's `runStaffEndpoint` returns: the same shape
 * the legacy `/api/staff/actions/:name` surface used (`{ok, data?, error?,
 * invalidate?}`), kept as a single type so callers can keep their existing
 * `if (result.ok) ...` glue.
 */
export type StaffActionResult =
    | { ok: true; data?: Record<string, unknown>; invalidate?: string[] }
    | { ok: false; error: string };

// ---- Batch review decisions (the single review write surface) ----
//
// One endpoint commits any number of review decisions in a single request.
// A singular accept/reject is just an array of one; the call-drafts panel
// stages several and confirms them together. Two decision shapes:
//   - `artefact` — act on a reviewable artefact by its kebab `kind` + id
//     (the same `ReviewAgentArtefact` Action the old per-kind routes used).
//   - `task` — act on an AgentTask: the server resolves the job's primary
//     artefact and acts on its Reviewable status, or audits at job level when
//     the job produced no artefact.
export const reviewDecisionVerbSchema = z.enum(['approve', 'reject', 'request_revision']);
export type ReviewDecisionVerb = z.infer<typeof reviewDecisionVerbSchema>;

// The two SEP-183 assignment schemas moved to `../schemas/review-payload.ts`,
// where the kind spec can import them. Re-exported here, because they are part
// of this contract's public surface and the callers are unchanged.
export {
    knowledgeAssignmentTargetTypeSchema,
    knowledgeAssignmentTargetSchema,
    type KnowledgeAssignmentTargetType,
    type KnowledgeAssignmentTarget,
} from '../schemas/review-payload';

/**
 * The knowledge items an assignment can point at (SEP-183 / ADR-0015). These
 * are Object Type KEYS, pinned verbatim, and they mirror `KNOWLEDGE_ITEM_TYPES`
 * in `vendor/septima-ontology-wealth/packages/ontology-wealth/src/objects/knowledge-assignment.ts`.
 *
 * `client-knowledge-document` joined the set in the second SEP-183 round. The
 * Client Knowledge page renders a hand-authored document beside the two ATLAS
 * artefact types, and a control that reached only the machine-written entries
 * would draw a line the reader cannot see.
 */
export const knowledgeItemTypeSchema = z.enum([
    'call-extracted-fact',
    'call-kb-note',
    'call-action-item',
    'client-knowledge-document',
]);
export type KnowledgeItemType = z.infer<typeof knowledgeItemTypeSchema>;

// The SEP-215 promotion schemas moved to `../schemas/review-payload.ts` for
// the same reason. Re-exported unchanged.
export {
    relationshipCounterpartySchema,
    relationshipPromotionSchema,
    type RelationshipCounterparty,
    type RelationshipPromotion,
} from '../schemas/review-payload';

// ---- The artefact decision: the kind discriminates the payload ----
//
// A decision used to be ONE object with `extra: Record<string, unknown>` and
// two named fields, `assignTo` and `promoteTo`, that had escaped onto the
// shared schema. Twenty-five kinds therefore declared three fields that four
// kinds read, and nothing typed the bag: a key that did not match was dropped
// in silence, which is how an approved monthly narrative discarded the chosen
// variant in production.
//
// The union below is DERIVED from the `payload` field of
// `REVIEWABLE_KIND_SPECS`. One arm for one kind. A kind that asks no second
// question has no `payload` field at all, in the type and on the wire.
//
// Each arm is `.strict()` on purpose. The wire is the second half of the
// guarantee: a compile error stops a wrong key in this repository, and a 400
// stops one from a stale browser tab. A dropped field is the failure this
// whole change exists to remove, so it must never be the quiet outcome.

/** The fields every artefact decision carries, whatever the kind. */
const artefactDecisionBaseShape = {
    target: z.literal('artefact'),
    id: z.string(),
    decision: reviewDecisionVerbSchema,
    // Reviewer note — required by the server for reject / request_revision
    // (it defaults an empty reject note to a placeholder; request_revision
    // demands a real one).
    reason: z.string().optional(),
};

function artefactDecisionOption(kind: ReviewableKind): z.ZodDiscriminatedUnionOption<'kind'> {
    const payload = REVIEWABLE_KIND_SPECS[kind].payload;
    return z
        .object({
            ...artefactDecisionBaseShape,
            kind: z.literal(kind),
            ...(payload ? { payload: payload.optional() } : {}),
        })
        .strict();
}

// The runtime options carry the per-kind literal only inside the `.map`, so
// the exported TYPE is declared below rather than inferred. A contract test
// walks every kind and pins the two against each other, which is the guard
// that keeps them honest.
//
// The assertion states non-emptiness, which `z.discriminatedUnion` demands and
// which a `.map` cannot express. The kind list is never empty: the same test
// asserts one arm for each kind.
const artefactDecisionOptions = reviewableKindSchema.options.map(artefactDecisionOption) as [
    z.ZodDiscriminatedUnionOption<'kind'>,
    ...z.ZodDiscriminatedUnionOption<'kind'>[],
];

/** The wire union itself. Its INFERRED type is loose, because the `.map`
 *  above erased the per-kind literal; `ArtefactReviewDecision` below is the
 *  declared type, and `reviewDecisionSchema` is what carries it. */
export const artefactReviewDecisionSchema = z.discriminatedUnion('kind', artefactDecisionOptions);

/**
 * One decision on one artefact, with the payload its kind declares.
 *
 * `payload?: never` on a kind that declares none is the compile error the old
 * `extra` bag could not give: a payload aimed at the wrong kind, a misspelt
 * field and a snake_case key are all rejected where they are written.
 */
export type ArtefactReviewDecision = {
    [K in ReviewableKind]: {
        target: 'artefact';
        kind: K;
        id: string;
        decision: ReviewDecisionVerb;
        reason?: string;
    } & ([ReviewPayload<K>] extends [never] ? { payload?: never } : { payload?: ReviewPayload<K> });
}[ReviewableKind];

export const taskReviewDecisionSchema = z.object({
    target: z.literal('task'),
    taskId: z.string(),
    // Tasks only approve/reject (request-revision is artefact-kind specific).
    decision: z.enum(['approve', 'reject']),
    reason: z.string().optional(),
});

export type TaskReviewDecision = z.infer<typeof taskReviewDecisionSchema>;

/**
 * One review decision. DECLARED, not inferred: the runtime union is built by
 * a `.map` over the kinds, which loses the per-kind literal, and the whole
 * value of this change is the per-kind literal.
 */
export type ReviewDecision = ArtefactReviewDecision | TaskReviewDecision;

// `z.union` and not `z.discriminatedUnion('target', …)`: zod 3 admits only a
// plain object as an option, and the artefact half is itself a discriminated
// union on `kind`. The two halves stay mutually exclusive on `target`, so the
// parse result is the same; only the shape of a failure message differs.
const reviewDecisionUnion = z.union([artefactReviewDecisionSchema, taskReviewDecisionSchema]);

/**
 * The wire schema, carrying the DECLARED type.
 *
 * `z.custom` names the type and delegates the checking to the real union
 * above, whose own inferred type the `.map` widened. `superRefine` forwards
 * every issue the union raised, so a bad decision still answers with the field
 * and the reason and not a bare "invalid input". This is the one seam where
 * the type is stated rather than inferred, and the contract test is what holds
 * the statement to the value: it parses one decision for every kind and
 * refuses every wrong payload.
 */
export const reviewDecisionSchema = z.custom<ReviewDecision>().superRefine((value, ctx) => {
    const parsed = reviewDecisionUnion.safeParse(value);
    if (parsed.success) return;
    for (const issue of parsed.error.issues) ctx.addIssue(issue);
});

// Per-decision outcome. `ref` echoes the artefact id / taskId so the caller can
// correlate a failure back to the row it staged.
export const reviewDecisionResultSchema = z.object({
    ref: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
});
export type ReviewDecisionResult = z.infer<typeof reviewDecisionResultSchema>;

export const reviewDecisionsRequestSchema = z.object({
    decisions: z.array(reviewDecisionSchema).min(1),
});

// The batch always answers 200 when the actor is admitted; per-decision
// failures live in `results` (partial success is honest, never aborts the
// batch). `summary` + `invalidate` (union of every decision's cache-bust keys)
// drive the one summary toast + React-Query invalidation in the web.
export const reviewDecisionsResponseSchema = z.object({
    ok: z.literal(true),
    results: z.array(reviewDecisionResultSchema),
    summary: z.object({ confirmed: z.number(), failed: z.number() }),
    invalidate: z.array(z.string()).optional(),
});
export type ReviewDecisionsResponse = z.infer<typeof reviewDecisionsResponseSchema>;

// The runner accepts either of two request shapes:
//   - `{ taskId }` — run an existing AgentJob by id.
//   - `{ type }`   — queue a fresh AgentJob for that task type, then run
//     it. The `type`-based form is used by the in-app trigger path
//     (AgentContext.startTask) and per-PRD Playwright fixtures so a
//     caller can fire an agent without first inserting a task.
export const agentRunRequestSchema = z.object({
    taskId: z.string().optional(),
    type: z.string().optional(),
    trigger: z.string().optional(),
    clientId: z.string().optional(),
    clientName: z.string().optional(),
    frontend: z.enum(['desk', 'ops', 'comply', 'view', 'global']).optional(),
    workflow: z.string().optional(),
    input: z.record(z.string(), z.unknown()).optional(),
    payload: z.record(z.string(), z.unknown()).optional(),
});

export const agentRunResponseSchema = z.object({
    ok: z.boolean(),
    taskId: z.string(),
    state: z.string().optional(),
    output: z.unknown().optional(),
    error: z.string().optional(),
});

// "Run X now". The runner picks the detector's source row (an open
// ontology candidate or an enriching working-entity row, depending on the
// detector mode), fires the real agent, and emits a real Finding.
// Returns the queued AgentTask id; the runner advances it.
//
// Originally `/api/demo/run-detector/:agent` (PRD #67); reframed as an
// ops affordance at `/api/ops/detectors/:agent/run` (ADR-0008, issue
// #92) — useful in production for re-running a detector after a config
// change or after backfilling source data. Persona-gated and audited
// at the controller.
export const runDetectorResponseSchema = z.object({
    ok: z.boolean(),
    agentTaskId: z.string().optional(),
    candidateId: z.string().optional(),
    error: z.string().optional(),
    // What a batch detector actually took on. MASON chunks the open breaks and
    // queues one job for each chunk, so `agentTaskId` names the first chunk
    // alone and states nothing about the size of the work. Before this field
    // the page counted the rows that it had loaded, and a press that beat the
    // results query made the page say "0 open breaks" while the server took on
    // one (SEP-250.4.2). The server chooses the breaks, so the server states
    // the count. Absent on a detector that runs no batch.
    breakCount: z.number().int().nonnegative().optional(),
    chunkCount: z.number().int().nonnegative().optional(),
});

// Typed `POST /api/agent/tasks` — queues an AgentJob and returns the
// composed task row. Used by AgentContext, ComposerTriggerButton,
// MonthlyNarrativePanel, and desk/calls-meetings.
export const agentTaskInsertSchema = z
    .object({
        id: z.string().optional(),
    })
    .catchall(z.unknown());

export const agentTaskRowSchema = z.object({ id: z.string() }).catchall(z.unknown());

// Typed `PATCH /api/agent/tasks/:id` for the same callers.

// Query for `GET /api/agent/tasks`. All optional; query values arrive as
// strings, so `limit`/`offset` are coerced. `status` / `type` accept a
// comma-separated list for an IN-filter (e.g. `?status=queued,running`); the
// handler splits on `,`. The web's existing pollers pass none of these, so
// the default (limit 200) is unaffected.
export const agentTaskListQuerySchema = z
    .object({
        status: z.string().optional(),
        type: z.string().optional(),
        surface: z.string().optional(),
        clientId: z.string().optional(),
        limit: z.coerce.number().int().positive().max(1000).optional(),
        offset: z.coerce.number().int().nonnegative().optional(),
    })
    .optional();
export type AgentTaskListQuery = z.infer<typeof agentTaskListQuerySchema>;

// Query for `GET /api/agent/worklist` — the caller's owner-scoped Agent Kanban
// (ADR-0010). Deliberately carries NO scope / owner / surface / clientId input:
// the worklist is identity-derived server-side from `ctx.actor`, never a
// parameter. Only pagination is accepted.
export const agentWorklistQuerySchema = z
    .object({
        limit: z.coerce.number().int().positive().max(1000).optional(),
        offset: z.coerce.number().int().nonnegative().optional(),
    })
    .optional();
export type AgentWorklistQuery = z.infer<typeof agentWorklistQuerySchema>;

// -----------------------------------------------------------------------
// Scheduled runs (WS-E) — the Kanban "Scheduled" tab's read surface.
//
// One flat row per scheduled unit of work, unioning named-agent schedule
// DECLARATIONS and enabled workflow schedules. `active` is honest about
// declared-vs-firing: an agent whose cron is actually registered is `true`,
// a declared-but-not-firing agent is `false`; a workflow is `active` when
// its `workflow:<id>` cron is live in the SchedulerRegistry.
// -----------------------------------------------------------------------
export const scheduledRunSchema = z.object({
    name: z.string(),
    kind: z.enum(['agent', 'workflow']),
    cron: z.string(),
    // Computed from the cron at read time; null when the cron is invalid.
    nextRunAt: z.string().nullable(),
    // Workflow: WorkflowSchedule.lastRunAt. Agent: best-effort newest scheduled AgentJob.
    lastRunAt: z.string().nullable(),
    surface: z.enum(['desk', 'ops', 'comply', 'view']),
    // Whether a cron is actually registered/firing for this row.
    active: z.boolean(),
    // workflowId (deep-link) for workflow rows; agentName for agent rows.
    ref: z.string().nullable(),
});
export type ScheduledRun = z.infer<typeof scheduledRunSchema>;

export const listScheduledRunsResponseSchema = z.object({
    scheduled: z.array(scheduledRunSchema),
});
export type ListScheduledRunsResponse = z.infer<typeof listScheduledRunsResponseSchema>;

export const agentContract = c.router(
    {
        run: {
            method: 'POST',
            path: '/agent/run',
            body: agentRunRequestSchema,
            responses: {
                200: agentRunResponseSchema,
                400: z.object({ error: z.string() }),
            },
            summary: 'Run an agent workflow',
        },
        insertTask: {
            method: 'POST',
            path: '/agent/tasks',
            body: agentTaskInsertSchema,
            responses: {
                200: agentTaskRowSchema,
                400: z.object({ error: z.string() }),
            },
            summary: 'Queue an AgentJob and return the composed task row (used by the web UI)',
        },
        listTasks: {
            method: 'GET',
            path: '/agent/tasks',
            // Server-side filters honoured by the handler. `status` / `type`
            // accept a single value or a comma-separated list (IN-filter);
            // `surface` / `clientId` push down to the resolver. `limit` /
            // `offset` paginate the composed result (default limit 200, so
            // existing web callers that pass nothing are unaffected).
            query: agentTaskListQuerySchema,
            responses: {
                200: z.object({ tasks: z.array(agentTaskRowSchema) }),
                400: z.object({ error: z.string() }),
            },
            summary:
                'List composed AgentTask rows the web UI polls (filtered + paginated server-side)',
        },
        listWorklist: {
            method: 'GET',
            path: '/agent/worklist',
            // The caller's owner-scoped Agent Kanban (ADR-0010). Self-scoped:
            // the server resolves the worklist from `ctx.actor` and accepts no
            // scope input, so this route can never return another user's (or
            // the firm-wide) list. The Agent Activity Log uses `listTasks`.
            query: agentWorklistQuerySchema,
            responses: {
                200: z.object({ tasks: z.array(agentTaskRowSchema) }),
                400: z.object({ error: z.string() }),
            },
            summary: "List the caller's owner-scoped agent worklist (identity-derived; ADR-0010)",
        },
        listScheduled: {
            method: 'GET',
            path: '/agent/scheduled',
            responses: {
                200: listScheduledRunsResponseSchema,
            },
            summary:
                'List scheduled runs (agent schedule declarations + enabled workflow schedules) for the Kanban "Scheduled" tab',
        },
        listFindings: {
            method: 'GET',
            path: '/agent/findings/:agent',
            pathParams: z.object({ agent: z.string() }),
            query: z.record(z.string(), z.string()).optional(),
            responses: {
                200: z.object({
                    findings: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
                }),
                400: z.object({ error: z.string() }),
            },
            summary: 'Per-detector finding rows the generic `<DetectorFindingFeed>` consumes',
        },
        listAgentSessions: {
            method: 'GET',
            path: '/agent/sessions',
            responses: {
                200: z.object({
                    sessions: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
                }),
                400: z.object({ error: z.string() }),
            },
            summary: 'List `agent_sessions` rows the AgentSessionsPanel polls',
        },
        listAgentTraces: {
            method: 'GET',
            path: '/agent/traces',
            query: z.object({ artefactId: z.string().optional() }).optional(),
            responses: {
                200: z.object({
                    traces: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
                }),
                400: z.object({ error: z.string() }),
            },
            summary: 'List `agent_traces` rows (glass-box trace)',
        },
        listAgentRows: {
            method: 'GET',
            path: '/agent/rows/:table',
            pathParams: z.object({ table: z.string() }),
            query: z.record(z.string(), z.string()).optional(),
            responses: {
                200: z.object({
                    rows: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
                }),
                400: z.object({ error: z.string() }),
            },
            summary:
                'Allow-listed read of agent-artefact tables used by ReviewQueueView, MonthlyNarrativePanel, and the composition-workbench',
        },

        // -----------------------------------------------------------------
        // Batch review decisions (the single review write surface) — typed,
        // persona-gated, actor-attributed. Supersedes the per-kind
        // approve/reject/request-revision routes AND the agent-task
        // approve/reject routes: every review decision (artefact or task,
        // singular or staged-batch) commits here in one request via the same
        // `ReviewAgentArtefact` Action.
        // -----------------------------------------------------------------
        reviewDecisions: {
            method: 'POST',
            path: '/agent/reviewables/decisions',
            body: reviewDecisionsRequestSchema,
            responses: {
                200: reviewDecisionsResponseSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
            },
            summary:
                'Apply a batch of review decisions (approve/reject/request-revision) over artefacts and/or tasks in one request',
        },

        // -----------------------------------------------------------------
        // Composer trigger (OLD_CODE.html #2 replacement for the seven
        // `trigger-<agent>` named actions).
        // -----------------------------------------------------------------
        triggerComposer: {
            method: 'POST',
            path: '/agent/composers/:agent/trigger',
            pathParams: z.object({ agent: composerAgentSchema }),
            // Per-agent body validation (mirrors `requireStringField` plumbing
            // in agent-triggers.ts) lives in the controller; the wire shape is
            // free-form so we don't have to mint seven separate endpoint
            // signatures.
            body: z.record(z.string(), z.unknown()),
            responses: staffActionResponsesNoNotFound,
            summary: 'Trigger a composer agent (queues an AgentTask of the matching type)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
